import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { concat, debounceTime, EMPTY, finalize, Observable, Subject, switchMap, tap } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { IResponse } from '../../../../core/interfaces/IResponse';
import { BaseComponent } from '../../../../core/layout/components/abstracts/base.component';
import { MainLayoutComponent } from '../../../../core/layout/layouts/main-layout/main-layout.component';
import { BroadcastChannelService } from '../../../../core/services/BroadcastChannelService';
import { ChipComponent } from '../../../../shared/components/chip/chip.component';
import {
  ConfirmationModalComponent
} from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import {
  ScrollableWrapperComponent
} from '../../../../shared/components/scrollable-wrapper/scrollable-wrapper.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { LocaleDatetimeFormatPipe } from '../../../../shared/pipes/locale-datetime-format.pipe';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { ICardType } from '../../../cards/core/interfaces/ICardType';
import { CardsStore } from '../../../cards/core/stores/CardsStore';
import {
  CardAmountPickerModalComponent
} from '../../../cards/modals/card-amount-picker-modal/card-amount-picker-modal.component';
import {
  CardCodeInputModalComponent
} from '../../../cards/modals/card-code-input-modal/card-code-input-modal.component';
import { CardDetailsModalComponent } from '../../../cards/modals/card-details-modal/card-details-modal.component';
import { KioskStore } from '../../../kiosk/core/stores/KioskStore';
import {
  PackageProductsSelectionModalComponent
} from '../../../kiosk/modals/package-products-selection-modal/package-products-selection-modal.component';
import { ReservationsStore } from '../../../reservations/core/stores/ReservationsStore';
import { WorkerRequirementService } from '../../../worker/core/services/WorkerRequirementService';
import { ShowsStore } from '../../../shows/core/stores/ShowsStore';
import { VouchersStore } from '../../../vouchers/core/stores/VouchersStore';
import {
  ExternalVouchersModalComponent
} from '../../../vouchers/modals/external-vouchers-modal/external-vouchers-modal.component';
import {
  VoucherCodeInputModalComponent
} from '../../../vouchers/modals/voucher-code-input-modal/voucher-code-input-modal.component';
import { CartItemTypes } from '../../core/enums/CartItemTypes';
import { CartPaymentMethods } from '../../core/enums/CartPaymentMethods';
import { CartStatuses } from '../../core/enums/CartStatuses';
import { DiscountType } from '../../core/enums/DiscountTypes';
import { ICart } from '../../core/interfaces/ICart';
import { ICartItem } from '../../core/interfaces/ICartItem';
import { ISelectedPosition } from '../../core/interfaces/ISelectedPosition';
import { CartService } from '../../core/services/CartService';
import { PaySeparatelyService } from '../../core/services/PaySeparatelyService';
import { CartStore } from '../../core/stores/CartStore';
import { PaySeparatelyStore } from '../../core/stores/PaySeparatelyStore';
import { CardPaymentBalanceInfoModalComponent } from '../../modals/card-payment-balance-info-modal/card-payment-balance-info-modal.component';
import { DiscountsModalComponent } from '../../modals/discounts-modal/discounts-modal.component';
import { PaySeparatelySelectionModalComponent } from '../../modals/pay-separately-selection-modal/pay-separately-selection-modal.component';
import {
  PriceCategoryPickerModalComponent
} from '../../modals/price-category-picker-modal/price-category-picker-modal.component';
import { SummaryModalComponent } from '../../modals/summary-modal/summary-modal.component';
import { AppStore } from '../../../../core/stores/AppStore';
import { Router } from '@angular/router';
import { IsVoucherStackablePipe } from "../../core/pipes/is-voucher-stackable.pipe";
import { CartItemDisplayTotalPipe } from '../../core/pipes/cart-item-display-total.pipe';
import { getDiscountCodeTotalReduction } from '../../core/utils/cart-item-display-total';
import { VoucherDetailsModalComponent } from "../../../vouchers/modals/voucher-details-modal/voucher-details-modal.component";
import { IVoucherDetails } from '../../../vouchers/core/interfaces/IVoucherDetails';
import { IVoucherTransaction } from '../../../vouchers/core/interfaces/IVoucherTransaction';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import { TOOLTIP_DELAY } from '../../../../shared/constants/tooltip';

@Component({
  selector: 'app-sidebar-cart',
  standalone: true,
  imports: [
    ContentBoxComponent,
    ScrollableWrapperComponent,
    NgTemplateOutlet,
    SummaryModalComponent,
    FormsModule,
    LoaderComponent,
    NumberToCurrencyPipe,
    PackageProductsSelectionModalComponent,
    TranslatePipe,
    PriceCategoryPickerModalComponent,
    ChipComponent,
    VoucherCodeInputModalComponent,
    ExternalVouchersModalComponent,
    CardCodeInputModalComponent,
    CardAmountPickerModalComponent,
    CardDetailsModalComponent,
    DiscountsModalComponent,
    LocaleDatetimeFormatPipe,
    StepperComponent,
    CardPaymentBalanceInfoModalComponent,
    PaySeparatelySelectionModalComponent,
    IsVoucherStackablePipe,
    CartItemDisplayTotalPipe,
    VoucherDetailsModalComponent,
    TooltipDirective
],
  templateUrl: './sidebar-cart.component.html',
  styleUrl: './sidebar-cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'd-contents'
  }
})
export class SidebarCartComponent extends BaseComponent implements AfterViewInit {

  protected readonly tooltipDelay = TOOLTIP_DELAY;

  @ViewChild('cardAmountPickerModalComponent') cardAmountPickerModalComponent: CardAmountPickerModalComponent;
  @ViewChild('packageProductsSelectionModal') packageProductsSelectionModal: PackageProductsSelectionModalComponent;
  @ViewChild('summaryModalComponent') summaryModalComponent: SummaryModalComponent;
  @ViewChild('paySeparatelySelectionModalComponent') paySeparatelySelectionModalComponent: PaySeparatelySelectionModalComponent;
  @ViewChild('voucherDetailsModalComponent') voucherDetailsModalComponent: VoucherDetailsModalComponent;
  @ViewChild('btnDetachCard') btnDetachCard: ElementRef<HTMLElement>;
  @ViewChild('btnDiscount') btnDiscount: ElementRef<HTMLElement>;

  public static instance: SidebarCartComponent;
  public readonly updateCartItem$ = new Subject<{ item: ICartItem, quantity: number }>();
  public readonly isCartLoading = signal(false);
  public readonly cartItemTypes = CartItemTypes;
  public readonly cartPaymentMethods = CartPaymentMethods;
  public readonly showsStore = inject(ShowsStore);
  public readonly cartStatuses = CartStatuses;
  public readonly missingCardPriceCategoryIds = computed<Set<number>>(() => {
    const cart = this.cartStore.cart();
    if (!cart) return new Set<number>();
    const attachedCardTypeIds = new Set(cart.customerCards.map(c => c.cardTypeId));
    const missingIds = new Set<number>();
    cart.showPriceCategories?.forEach(({ categories }) => {
      categories.forEach(pc => {
        if (pc.cardTypeId && !attachedCardTypeIds.has(pc.cardTypeId)) {
          missingIds.add(pc.id);
        }
      });
    });
    return missingIds;
  });

  public readonly paySeparatelyStore = inject(PaySeparatelyStore);
  public readonly cartStore = inject(CartStore);
  public readonly reservationsStore = inject(ReservationsStore);
  public readonly isPaySeparatelyModeEnabled = (): boolean =>
    this.paySeparatelyStore.isPaySeparatelyModeEnabled();
  public readonly selectedItemsTotal = (): number =>
    this.paySeparatelyStore.selectedItemsTotal();

  private readonly _cinemas = this.employeesStore.myEmployee().cinemas;

  public readonly cardPaymentAmounts = computed<Map<number, number>>(() => {
    const cart = this.cartStore.cart();
    const amountsMap = new Map<number, number>();

    if (!cart) {
      return amountsMap;
    }

    cart.items
      .filter(item => item.itemType === CartItemTypes.CashCineCard && item.cardId != null)
      .forEach(item => {
        amountsMap.set(item.cardId!, Math.abs(item.totalPrice));
      });

    return amountsMap;
  });

  public readonly appliedDiscountCode = computed<{ code: string; amount: number } | null>(() => {
    const cart = this.cartStore.cart();
    const discount = cart?.discount;
    if (discount?.type === DiscountType.DiscountCode && discount.code) {
      return {
        code: discount.code,
        amount: getDiscountCodeTotalReduction(discount, cart.items)
      };
    }
    return null;
  });

  protected readonly paySeparatelyService = inject(PaySeparatelyService);
  protected readonly vouchersStore = inject(VouchersStore);

  private readonly _cartService = inject(CartService);
  private readonly _kioskStore = inject(KioskStore);
  private readonly _broadcastChannelService = inject(BroadcastChannelService);
  private readonly _router = inject(Router);
  private readonly _cardsStore = inject(CardsStore);
  private readonly _workerRequirement = inject(WorkerRequirementService);

  public ngOnInit(): void {
    this.paySeparatelyService.initFromStorage();

    this.paySeparatelyService.paySeparatelyModeChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());

    this.updateCartItem$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        switchMap(({ item, quantity: newQuantity }) => {
          if (!this.paySeparatelyStore.isPaySeparatelyModeEnabled()) {
            return this._cartService.updateCartItem([{ ...item, quantity: newQuantity }]);
          }

          const items = this.paySeparatelyStore.getValue(item.itemId, item.position);
          const hiddenItemsQuantity = items.length;
          const quantity = Math.max(0, item.quantity - hiddenItemsQuantity);

          if (item.quantity === quantity) {
            return this._cartService.updateCartItem([{ ...item, quantity: newQuantity }]);
          } else {
            this.paySeparatelyStore.removeItemByKey(items[0]);
            return EMPTY;
          }
        })
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`update-cart-item/notifications/${error.message}.Message`)
          );
        }
      });

    if (this.isCustomerScreen) {
      this._broadcastChannelService.paySeparatelySelectionChanged$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          debounceTime(50)
        )
        .subscribe((data: { selectedPositions: ISelectedPosition[]; isPaySeparatelyModeEnabled: boolean }) => {
          this.paySeparatelyStore.loadFromStorage();
          this.paySeparatelyService.setPaySeparatelyMode(data.isPaySeparatelyModeEnabled);
          this.cdr.detectChanges();
        });
    }
  }

  public ngAfterViewInit(): void {
    SidebarCartComponent.instance = this;
  }

  public get isPartialPaymentPossible(): boolean {
    return this.paySeparatelyService.isPartialPaymentPossible;
  }

  public get hasExternalVoucherTypes(): boolean {
    return this.vouchersStore.externalVoucherTypes().length > 0;
  }

  public getCardTypeForCard(cardTypeId: number) {
    return this._cardsStore.allCardTypes().find(ct => ct.cardTypeId === cardTypeId) ?? null;
  }

  public isAuthorizeOnly(cardType: ICardType): boolean {
    return this._cardsStore.isAuthorizeOnly(cardType);
  }

  public isAuthorizeOnlyCard(cardTypeId: number): boolean {
    const cardType = this.getCardTypeForCard(cardTypeId);
    return !!cardType && this.isAuthorizeOnly(cardType);
  }

  public getPositionQuantity(itemId: number, position: number): number {
    return this.paySeparatelyStore.getSelectedPositions()?.find(item => item.itemId === itemId && item.position === position)?.quantity || 0;
  }

  public hasSelectedTicketsInGroup(group: { tickets: { itemId: number; position: number }[] }): boolean {
    return group.tickets.some(t => !this.paySeparatelyStore.isHidden(t.itemId, t.position));
  }

  public clearCart(btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService.clearCart()
      .pipe(
        tap(() => this.summaryModalComponent?.resetCheckoutProcessingState()),
        finalize(() => {
          this.cleanupAfterCartOperation([btn]);
        })
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.summaryModalComponent?.resetCheckoutProcessingState();
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`clear-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public openPaySeparatelyModal(): void {
    if (!this.paySeparatelyStore.isPaySeparatelyModeEnabled()) {
      this.paySeparatelyService.enablePaySeparatelyMode();
    }

    if (this.paySeparatelyStore.isPaySeparatelyModeEnabled()) {
      this.paySeparatelySelectionModalComponent.open();
    }
  }


  public detachCard(cardId: number): void {
    if (this.isPreviewMode) {
      return;
    }

    this._cartService.detachCardFromCart(cardId)
      .subscribe({
        next: () => {
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public openSummaryModal(): void {
    this.openWorkerStatusConfirmationModal(() => this.summaryModalComponent.open());
  }

  public clearCartAndDetachCard(): void {
    const btns = [this.btnDetachCard?.nativeElement, this.btnDiscount?.nativeElement].filter(Boolean);
    btns.forEach(btn => btn.classList.add('disabled'));

    const hasDiscounts = !!this.cartStore.cart().discount;
    const customerCards = this.cartStore.customerCards();

    const apiCalls$: Observable<IResponse<ICart>>[] = [];

    if (hasDiscounts) {
      apiCalls$.push(this._cartService.removeAllDiscountsFromCart());
    }

    if (customerCards.length > 0) {
      customerCards.forEach(card => {
        apiCalls$.push(this._cartService.detachCardFromCart(card.cardId));
      });
    }

    if (apiCalls$.length === 0) {
      this.cleanupAfterCartOperation(btns);
      return;
    }

    concat(...apiCalls$)
      .pipe(
        finalize(() => {
          this.cleanupAfterCartOperation(btns);
        })
      )
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`clear-cart-and-detach-card/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public removeDiscountFromCart(): void {
    if (this.isPreviewMode) {
      return;
    }

    this._cartService.removeAllDiscountsFromCart()
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`remove-all-discounts/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public processQuickCashPayment(): void {
    if (!this.terminalsStore.isPaymentMethodAllowed(CartPaymentMethods.Cash)) {
      return;
    }

    this.openWorkerStatusConfirmationModal(
      () => {
        const amountToPay = this.paySeparatelyStore.isPaySeparatelyModeEnabled()
          ? this.paySeparatelyStore.selectedItemsTotal()
          : this.cartStore.cart().remainingAmount;
        this.summaryModalComponent.addPayment(
          CartPaymentMethods.Cash,
          amountToPay,
          {},
          () => this.summaryModalComponent.onCheckoutButtonClick(true)
        );
      }
    );
  }

  public processQuickCardPayment(): void {
    this.openWorkerStatusConfirmationModal(() => {
      setTimeout(() => {
        if (this.isEcStandalone
          || MainLayoutComponent.instance.workerConnectionStatus() === 'green') {
          this.summaryModalComponent.choosePayment(CartPaymentMethods.Card, { autoClose: true });
        }
      });
    });
  }

  public processQuickInvoicePayment(): void {
    if (!this.terminalsStore.isPaymentMethodAllowed(CartPaymentMethods.Invoice)) {
      return;
    }

    this.openWorkerStatusConfirmationModal(
      () => this.summaryModalComponent.startQuickInvoicePayment()
    );
  }

  public get areSummaryButtonsDisabled(): boolean {
    return this.cartStore.cart().items.length === 0 ||
      !this.cartStore.cart() ||
      this.summaryModalComponent?.isPaymentAdding() ||
      this.summaryModalComponent?.isCheckoutProcessing() ||
      this.summaryModalComponent?.isCartFinishing();
  }

  public removeItemFromCart(itemId: number, position: number, btn: HTMLButtonElement): void {
    btn.classList.add('disabled');

    this._cartService
      .removePositionFromCart(itemId, position)
      .pipe(finalize(() => btn.classList.remove('disabled')))
      .subscribe({
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`remove-item-from-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  public openPackageProductsSelectionModal(item: ICartItem): void {
    this.packageProductsSelectionModal.open(
      this._kioskStore.kioskProductsAndPackages().package.find(x => x.id === item.itemId),
      item
    );
  }

  public onVoucherDetailsFromCardAttach(event: { voucherDetails: IVoucherDetails; transactions: IVoucherTransaction[] }): void {
    this.voucherDetailsModalComponent.open(event.voucherDetails, event.transactions);
  }

  public openShow(showId: number, cinemaId: number): void {
    this._router.navigateByUrl(`/shows/${showId}`.langUrl())
      .then(() => {
        if (!this.showsStore.shows().find(s => s.id === showId)) {
          this.terminalsStore.changedCinema.set(
            cinemaId === this.terminalsStore.selectedTerminal().cinemaId
              ? null
              : this._cinemas.find(c => c.id === cinemaId)
          );
          AppStore.shouldPreventAppReload.set(false);
          location.reload();
        }
      });
  }

  private openWorkerStatusConfirmationModal(cb: () => void): void {
    if (!this._workerRequirement.isWorkerRequired()
      || MainLayoutComponent.instance.workerConnectionStatus() === 'green') {
      cb();
    } else {
      ConfirmationModalComponent.openConfirmationModal(
        'Worker Status'.trans(`cart/sidebar/worker-not-connected-alert-modal.Title`),
        'Worker is disconnected, do you want to continue?'.trans(`cart/sidebar/worker-not-connected-alert-modal.Text`),
        this.destroyRef,
        () => cb()
      );
    }
  }

  private cleanupAfterCartOperation(btns: HTMLElement[]): void {
    btns.forEach(btn => btn.classList.remove('disabled'));
    this.reservationsStore.rebookReservation.set(null);
  }
}
